import React, { useContext, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { AuthenticationContext } from '../../components/AuthenticationContext';
import PlayerTypeSettings from './PlayerTypeSettings';
import { types } from './PlayerTypes';
import { PlayerForm, initialState, toPlayer, toForm } from './PlayerForm';
import Login from '../Login';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
} from '@mui/material';
import AllowedDomains from './AllowedDomains';
import { useMutation, useQuery } from '@apollo/client';
import { GQL_GET_PLAYER } from '../../graphql/queries/players';
import { GQL_CREATE_PLAYER, GQL_DELETE_PLAYER, GQL_UPDATE_PLAYER } from '../../graphql/mutations/players';
import { GQL_GET_CAMERA_DETAIL, GQL_GET_CAMERA_INFO_FOR_NEW_PLAYER } from '../../graphql/queries/cameras';
import UCheckbox from '../../utils/UCheckbox';
import USelect from '../../utils/USelect';
import UTextField2 from '../../utils/UTextField2';

export default function PlayerSettings() {
  const { id, cameraId } = useParams<{ id: string; cameraId: string }>();
  const methods = useForm<PlayerForm>({
    defaultValues: { ...initialState, camera: cameraId },
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    criteriaMode: 'all',
    shouldFocusError: true,
    shouldUnregister: false,
  });
  const { handleSubmit, reset } = methods;
  const history = useHistory();
  const { authorized, removeToken } = useContext(AuthenticationContext);

  const isNew = id == 'new';
  const [loading, setLoading] = useState(!isNew);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [speeds, setSpeeds] = useState<number[]>([]);
  const [positions, setPositions] = useState<string[]>([]);
  const [cameraName, setCameraName] = useState();
  const [clientName, setClientName] = useState();
  const [clientId, setClientId] = useState();
  const [cameraInfo, setCameraInfo] = useState();
  const variables = isNew ? { cameraId: cameraId } : { playerId: id };
  const query = isNew ? GQL_GET_CAMERA_INFO_FOR_NEW_PLAYER : GQL_GET_PLAYER;
  useQuery(query, {
    onError(error) {
      if (error.graphQLErrors.findIndex((err) => err?.extensions?.code == 'UNAUTHENTICATED') != -1) {
        removeToken();
      } else {
        setError(`Failed to load data ${error.message}`);
        console.error(error);
      }
      setLoading(false);
    },
    onCompleted(data) {
      console.log('data', data);
      setLoading(false);
      if (data.player) {
        const positions = data.player.camera.panoramas.map(({ ref }: { ref: any }) => ref);
        data.player.camera.frameCaptures.forEach((capture: any) => {
          positions.push(capture.captures.map(({ ref }: { ref: any }) => ref));
        });
        setCameraName(data.player.camera.name);
        setClientName(data.player.camera.client.name);
        setClientId(data.player.camera.client.id);
        setSpeeds(data.player.camera.record.fastPlaybacks.map(({ speed }: { speed: any }) => speed));
        setPositions(positions.flat());
        setCameraInfo(data.player.camera);
        const form = toForm(data.player);
        reset(form);
      } else if (data.camera) {
        const positions = data.camera.panoramas.map(({ ref }: { ref: any }) => ref);
        data.camera.frameCaptures.forEach((capture: any) => {
          positions.push(capture.captures.map(({ ref }: { ref: any }) => ref));
        });
        setCameraName(data.camera.name);
        setClientName(data.camera.client.name);
        setClientId(data.camera.client.id);
        setSpeeds(data.camera.record.fastPlaybacks.map(({ speed }: { speed: any }) => speed));
        setPositions(positions.flat());
        setCameraInfo(data.camera);
      }
    },
    variables,
    skip: !authorized,
  });

  const [deletePlayer] = useMutation(GQL_DELETE_PLAYER, {
    refetchQueries: [
      {
        query: GQL_GET_CAMERA_DETAIL,
        variables: {
          cameraId: cameraId,
        },
      },
    ],
    awaitRefetchQueries: true,
  });
  const [createPlayer] = useMutation(GQL_CREATE_PLAYER, {
    refetchQueries: [
      {
        query: GQL_GET_CAMERA_DETAIL,
        variables: {
          cameraId: cameraId,
        },
      },
    ],
    awaitRefetchQueries: true,
  });
  const [updatePlayer] = useMutation(GQL_UPDATE_PLAYER, {
    refetchQueries: [
      {
        query: GQL_GET_CAMERA_DETAIL,
        variables: {
          cameraId: cameraId,
        },
      },
      {
        query: GQL_GET_PLAYER,
        variables: {
          playerId: id,
        },
      },
    ],
    awaitRefetchQueries: true,
  });

  const [del, setDel] = React.useState(false);

  if (!authorized) return <Login />;

  const handleDelClose = () => {
    setDel(false);
  };

  const onDelClick = () => {
    setDel(true);
  };

  const onDelete = () => {
    setDel(false);
    const variables = {
      playerId: id,
    };
    deletePlayer({ variables })
      .then(() => {
        setLoading(false);
        setError('');
        setSuccess('Player deleted!');
        history.push(`/camera/${cameraId}`);
      })
      .catch((err) => {
        setLoading(false);
        setError('Failed to delete player');
        console.error(err);
      });
  };

  const onSubmit = (form: PlayerForm) => {
    const variables: any = {
      input: toPlayer(form),
    };
    if (id == 'new') {
      delete variables.input.id;
      createPlayer({ variables })
        .then((result) => {
          setLoading(false);
          setError('');
          setSuccess('Player created!');
          history.push(`/camera/${cameraId}/player/${result.data.createPlayer.id}`);
        })
        .catch((err) => {
          setLoading(false);
          setError('Failed to create panorama');
          console.error(err);
        });
    } else {
      updatePlayer({ variables })
        .then(() => {
          setLoading(false);
          setError('');
          setSuccess('Player updated!');
        })
        .catch((err) => {
          setLoading(false);
          setError(`Failed to update player ${err.message}`);
          console.error(err);
        });
    }
  };

  return (
    <Card>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <CardContent>
            <Grid container alignItems="center" spacing={2}>
              <Grid container spacing={2} sx={{ margin: '0 0 20px 0' }} alignItems="center">
                <Grid item xs={12} sm>
                  <h4 style={{ margin: 0 }}>
                    <span style={{ cursor: 'pointer' }} onClick={() => history.push(`/`)}>
                      {clientName}
                    </span>{' '}
                    -{' '}
                    <span style={{ cursor: 'pointer' }} onClick={() => history.push(`/camera/${cameraId}`)}>
                      {cameraName}
                    </span>{' '}
                  </h4>
                  <h1 style={{ margin: 0 }}>Player Settings</h1>
                </Grid>
                <Grid item xs="auto" sm="auto">
                  {id != 'new' && (
                    <Button type="button" color="neutral" disabled={loading} onClick={onDelClick} variant="contained">
                      Delete
                    </Button>
                  )}
                </Grid>
                <Grid item flexGrow={{ xs: 1, sm: 0 }} flexShrink={{ sm: 1 }}></Grid>
                <Grid item xs="auto" sm="auto">
                  <Button type="submit" color="secondary" disabled={loading} variant="contained">
                    {id == 'new' ? 'Add' : 'Update'}
                  </Button>
                </Grid>
              </Grid>
            </Grid>

            {loading && (
              <Box display="flex" minHeight="300px" justifyContent="center" alignItems="center">
                <CircularProgress />
              </Box>
            )}
            {success && <h3>{success}</h3>}
            {error && <h3>{error}</h3>}
            <Grid container spacing={2}>
              {!loading && (
                <>
                  <Grid item sm={6} xs={12} container spacing={2} alignItems="flex-start" alignContent="flex-start">
                    <Grid item xs={12}>
                      <UTextField2
                        fullWidth
                        required
                        label="Name"
                        name="name"
                        shrink={true}
                        options={{
                          required: 'Name is required',
                          minLength: { value: 3, message: 'Name must have at least 3 characters' },
                          maxLength: { value: 50, message: 'Name must have at max 50 characters' },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Paper style={{ backgroundColor: '#eee', padding: '16px 10px' }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <USelect
                              fullWidth
                              name="type"
                              defaultValue="hdrelay-minimal"
                              rules={{
                                required: 'Player Type is required',
                              }}
                              label="Type"
                            >
                              {Object.entries(types).map(([type, name]: [type: string, name: string]) => {
                                return (
                                  <MenuItem value={type} key={type}>
                                    {name}
                                  </MenuItem>
                                );
                              })}
                            </USelect>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                  </Grid>
                  <Grid item sm={6} xs={12} container spacing={2} alignItems="flex-start" alignContent="flex-start">
                    <Grid item xs={12}>
                      <Paper style={{ backgroundColor: '#dfd', padding: '16px 10px' }}>
                        <Grid container spacing={2}>
                          <AllowedDomains />
                        </Grid>
                      </Paper>
                    </Grid>
                    <Grid item xs={12}>
                      <UCheckbox name="active" label="Active" defaultValue={true} color="secondary" />
                    </Grid>
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <PlayerTypeSettings
                  playerId={id}
                  speeds={speeds}
                  positions={positions}
                  client={clientId}
                  camera={cameraInfo}
                />
              </Grid>
            </Grid>
          </CardContent>
        </form>
      </FormProvider>
      <Dialog
        open={del}
        onClose={handleDelClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Are you sure?</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">This operation can not be undone.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDelClose} color="secondary">
            Disagree
          </Button>
          <Button onClick={onDelete} color="neutral" autoFocus>
            Agree
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
